import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  Textarea,
  Tooltip,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { fetchController } from "../../utils/FetchController/fetchController";
import { getHostDomain } from "../../utils/utils";

const baseUrl = getHostDomain();

// Extract variable placeholders like {{1}}, {{2}} from a template component list
function extractVariables(components) {
  const vars = [];
  for (const comp of components || []) {
    if (!comp.text) continue;
    const matches = [...comp.text.matchAll(/\{\{(\d+)\}\}/g)];
    for (const m of matches) {
      if (!vars.find((v) => v.index === m[1])) {
        vars.push({ index: m[1], value: "" });
      }
    }
  }
  return vars;
}

// Resolve {{1}}, {{2}} in template text with provided variable values
function resolveTemplateText(components, variables) {
  let text = "";
  for (const comp of components || []) {
    if (comp.type === "BODY" && comp.text) {
      text = comp.text;
      for (const v of variables) {
        text = text.replaceAll(`{{${v.index}}}`, v.value || `{{${v.index}}}`);
      }
    }
  }
  return text;
}

const SUPPORTED_CHANNELS = [
  { type: "whatsapp", label: "WhatsApp", icon: "💬" },
  { type: "sms",      label: "SMS",       icon: "📱" },
];

export default function InitiateConversationModal({
  isOpen,
  onClose,
  contact = null,
  contacts = null,
  preselectedChannel = null,
}) {
  const baseUrlLocal = baseUrl;

  // Resolve to an array of contacts
  const resolvedContacts = contacts ?? (contact ? [contact] : []);
  const isBulk = resolvedContacts.length > 1;

  // Step 1: channel type selection
  const [selectedChannelType, setSelectedChannelType] = useState(preselectedChannel);

  const [widgets, setWidgets] = useState([]);
  const [selectedWidget, setSelectedWidget] = useState(null);
  const [loadingWidgets, setLoadingWidgets] = useState(true);

  // "message" | "template"
  const [mode, setMode] = useState(null);

  // free-form
  const [message, setMessage] = useState("");

  // template
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [variables, setVariables] = useState([]);

  const [sending, setSending] = useState(false);

  // Header info
  const singleContact = resolvedContacts.length === 1 ? resolvedContacts[0] : null;
  const contactName = singleContact
    ? [singleContact?.first_name, singleContact?.last_name].filter(Boolean).join(" ") || singleContact?.email || "Contact"
    : null;
  const lastSeen = singleContact?.last_seen
    ? new Date(singleContact.last_seen).toLocaleString()
    : "Never";

  // Auto-select channel when preselectedChannel changes (e.g. bulk from dropdown)
  useEffect(() => {
    if (preselectedChannel) {
      setSelectedChannelType(preselectedChannel);
    }
  }, [preselectedChannel]);

  // Load widgets on open
  useEffect(() => {
    const load = async () => {
      setLoadingWidgets(true);
      try {
        let page = 1;
        let allWidgets = [];
        while (true) {
          const res = await fetchController(
            baseUrlLocal + `/api/v1/widget/get_my_details?size=100&page=${page}`,
            "GET"
          );
          const items = res?.data?.items || [];
          allWidgets = [...allWidgets, ...items];
          if (items.length < 100) break;
          page++;
        }
        setWidgets(allWidgets);
      } catch {
        toast.error("Failed to load widgets.");
      } finally {
        setLoadingWidgets(false);
      }
    };
    load();
  }, [baseUrlLocal]);

  // When widget changes and mode is "template", load templates
  useEffect(() => {
    if (mode === "template" && selectedWidget?.widget_type === "whatsapp") {
      loadTemplates(selectedWidget.id);
    }
  }, [selectedWidget, mode]);

  const loadTemplates = async (widgetId) => {
    setLoadingTemplates(true);
    setTemplates([]);
    setSelectedTemplate(null);
    setVariables([]);
    try {
      const res = await fetchController(
        baseUrlLocal + `/api/v1/messaging/whatsapp/templates?widget_id=${widgetId}`,
        "GET"
      );
      setTemplates(res?.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load templates.");
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleTemplateSelect = (templateName) => {
    const tpl = templates.find((t) => t.name === templateName);
    setSelectedTemplate(tpl || null);
    setVariables(tpl ? extractVariables(tpl.components) : []);
  };

  const handleSend = async () => {
    if (!selectedWidget) {
      toast.error("Select a widget first.");
      return;
    }
    if (mode === "message" && !message.trim()) {
      toast.error("Enter a message.");
      return;
    }
    if (mode === "template" && !selectedTemplate) {
      toast.error("Select a template.");
      return;
    }

    setSending(true);
    try {
      if (isBulk) {
        // Bulk: POST /api/v1/messaging/{widgetId}/contacts
        const body = {
          contact_ids: resolvedContacts.map((c) => c.id),
          message: message.trim(),
        };
        const res = await fetchController(
          baseUrlLocal + `/api/v1/messaging/${selectedWidget.id}/contacts`,
          "POST",
          body
        );
        if (res?.status === "queued" || res?.status === "ok" || res?.detail) {
          toast.success(`Messages queued for ${resolvedContacts.length} contacts.`);
          onClose();
        } else {
          toast.error(res?.message || "Failed to queue messages.");
        }
      } else {
        // Single contact
        const body = {
          contact_id: resolvedContacts[0].id,
          start_new_conversation: true,
        };

        if (mode === "message") {
          body.message = message.trim();
        } else {
          body.message = "";
          body.widget_parameters = {
            whatsapp: {
              template_name: selectedTemplate.name,
              language_code: selectedTemplate.language,
              components: variables.length
                ? [
                    {
                      type: "body",
                      parameters: variables.map((v) => ({
                        type: "text",
                        text: v.value,
                      })),
                    },
                  ]
                : [],
            },
          };
        }

        const res = await fetchController(
          baseUrlLocal + `/api/v1/messaging/${selectedWidget.id}`,
          "POST",
          body
        );

        if (res?.status === "sent" || res?.status === "ok") {
          toast.success("Message sent successfully.");
          onClose();
        } else {
          toast.error(res?.message || "Failed to send message.");
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Failed to send message.");
    } finally {
      setSending(false);
    }
  };

  const isWhatsApp = selectedWidget?.widget_type === "whatsapp";
  const showTemplate = isWhatsApp && !isBulk;
  const filteredWidgets = selectedChannelType
    ? widgets.filter((w) => w.widget_type === selectedChannelType)
    : [];
  const bodyText = selectedTemplate
    ? resolveTemplateText(selectedTemplate.components, variables)
    : "";

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Initiate Conversation</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex direction="column" gap={4}>
            {/* Contact info */}
            <Box p={3} borderRadius="md" bg="gray.50">
              {isBulk ? (
                <Text fontWeight="bold">{resolvedContacts.length} contacts selected</Text>
              ) : (
                <>
                  <Text fontWeight="bold">{contactName}</Text>
                  <Text fontSize="sm" color="gray.500">
                    Last seen: {lastSeen}
                  </Text>
                </>
              )}
            </Box>

            {/* Step 1: Channel type */}
            <FormControl isRequired>
              <FormLabel>Channel</FormLabel>
              <Flex gap={3}>
                {SUPPORTED_CHANNELS.map((ch) => (
                  <Button
                    key={ch.type}
                    flex={1}
                    colorScheme={selectedChannelType === ch.type ? "blue" : "gray"}
                    variant={selectedChannelType === ch.type ? "solid" : "outline"}
                    onClick={() => {
                      setSelectedChannelType(ch.type);
                      setSelectedWidget(null);
                      setMode(null);
                      setMessage("");
                      setSelectedTemplate(null);
                      setVariables([]);
                    }}
                  >
                    {ch.icon} {ch.label}
                  </Button>
                ))}
              </Flex>
            </FormControl>

            {/* Step 2: Widget selector (filtered by channel type) */}
            {selectedChannelType && (
              <FormControl isRequired>
                <FormLabel>Widget</FormLabel>
                <Select
                  placeholder={loadingWidgets ? "Loading widgets…" : `Select a ${selectedChannelType} widget`}
                  isDisabled={loadingWidgets}
                  value={selectedWidget?.id || ""}
                  onChange={(e) => {
                    const w = filteredWidgets.find((ww) => ww.id === e.target.value);
                    setSelectedWidget(w || null);
                    setMode(null);
                    setMessage("");
                    setSelectedTemplate(null);
                    setVariables([]);
                  }}
                >
                  {filteredWidgets.length === 0 && !loadingWidgets ? (
                    <option disabled>No {selectedChannelType} widgets configured</option>
                  ) : (
                    filteredWidgets.map((w) => (
                      <option key={w.id} value={w.id}>{w.name}</option>
                    ))
                  )}
                </Select>
              </FormControl>
            )}

            {/* Step 3: Mode buttons */}
            {selectedWidget && (
              <Flex gap={3}>
                <Button
                  flex={1}
                  colorScheme={mode === "message" ? "blue" : "gray"}
                  variant={mode === "message" ? "solid" : "outline"}
                  onClick={() => { setMode("message"); setSelectedTemplate(null); setVariables([]); }}
                >
                  Send Message
                </Button>
                {showTemplate && (
                  <Button
                    flex={1}
                    colorScheme={mode === "template" ? "blue" : "gray"}
                    variant={mode === "template" ? "solid" : "outline"}
                    onClick={() => { setMode("template"); setMessage(""); }}
                  >
                    Send Template
                  </Button>
                )}
              </Flex>
            )}

            {/* Free-form message */}
            {mode === "message" && (
              <FormControl isRequired>
                <FormLabel>Message</FormLabel>
                <Textarea
                  placeholder="Type your message…"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                />
              </FormControl>
            )}

            {/* Template picker (single contact only) */}
            {mode === "template" && (
              <>
                <FormControl isRequired>
                  <FormLabel>Template</FormLabel>
                  <Select
                    placeholder={loadingTemplates ? "Loading templates…" : "Select a template"}
                    isDisabled={loadingTemplates}
                    onChange={(e) => handleTemplateSelect(e.target.value)}
                  >
                    {templates.map((t) => (
                      <option key={t.name} value={t.name}>
                        {t.name} <Badge ml={1}>{t.category}</Badge>
                      </option>
                    ))}
                  </Select>
                </FormControl>

                {/* Variable inputs */}
                {variables.map((v, i) => (
                  <FormControl key={v.index} isRequired>
                    <FormLabel>Variable {`{{${v.index}}}`}</FormLabel>
                    <Input
                      placeholder={`Value for {{${v.index}}}`}
                      value={v.value}
                      onChange={(e) => {
                        const updated = [...variables];
                        updated[i] = { ...updated[i], value: e.target.value };
                        setVariables(updated);
                      }}
                    />
                  </FormControl>
                ))}

                {/* Preview */}
                {bodyText && (
                  <Box p={3} bg="gray.50" borderRadius="md" fontSize="sm">
                    <Text fontWeight="semibold" mb={1}>Preview:</Text>
                    <Text whiteSpace="pre-wrap">{bodyText}</Text>
                  </Box>
                )}
              </>
            )}
          </Flex>
        </ModalBody>

        <ModalFooter gap={2}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            colorScheme="blue"
            isLoading={sending}
            isDisabled={!mode || !selectedWidget}
            onClick={handleSend}
          >
            Send
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
